/**
 * The Library's Has master filter (1.2.0): "Has master: Textless", "Has master: UHD", from the variant list and the
 * format classes, as master tags in the filters (f.toggle('masters', tag); MASTER_TAG, variantTag, formatTag, pairTag
 * in contracts), and their chips above the results. Tags with the same prefix are alternatives (Textless or Clean
 * feed); a variant and a format must both hold (Textless and UHD, possibly on different masters).
 */
import { Check, X } from 'lucide-react';
import type { MasterVariantDTO } from '@prism/contracts';
import { FORMAT_CLASSES, PROGRAMME_VARIANT_KEY, formatTag, variantTag } from '@prism/contracts';
import { useMasterVariants } from '../../api/hooks';
import type { LibraryFilterState } from './useLibraryFilters';

/** "Textless", "UHD", "Textless · UHD" for a tag; null for one the list no longer has. */
export function masterTagLabel(tag: string, variants: readonly Pick<MasterVariantDTO, 'id' | 'name'>[]): string | null {
  if (tag.startsWith('f:')) return tag.slice(2);
  if (tag.startsWith('v:')) return variants.find((v) => v.id === tag.slice(2))?.name ?? null;
  if (tag.startsWith('m:')) {
    const [, id, c] = tag.split(':');
    const v = variants.find((x) => x.id === id);
    return v ? `${v.name} · ${c}` : null;
  }
  return null;
}

/** The facet in the filter pane. */
export function MasterFacet({ f }: { f: LibraryFilterState }) {
  const variants = useMasterVariants().data?.items ?? [];
  const selected = f.selectedFor('masters');
  const options: { tag: string; label: string }[] = [
    ...variants.filter((v) => v.key !== PROGRAMME_VARIANT_KEY || selected.includes(variantTag(v.id))).map((v) => ({ tag: variantTag(v.id), label: v.name })),
    ...FORMAT_CLASSES.map((c) => ({ tag: formatTag(c), label: c === 'Audio' ? 'Audio only' : c })),
  ];
  return (
    <div className="facet master-facet" data-testid="master-filter">
      <h3 title="Assets with a master of this variant or format (the primary master included)">Has master</h3>
      {options.map((o) => (
        <button key={o.tag} className="facet-option" aria-pressed={selected.includes(o.tag)} onClick={() => f.toggle('masters', o.tag)} data-testid={`master-filter-${o.tag}`}>
          <span className="check">{selected.includes(o.tag) && <Check size={11} strokeWidth={3} />}</span>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The chosen tags as removable chips in the results' toolbar. */
export function MasterFilterChips({ f }: { f: LibraryFilterState }) {
  const selected = f.selectedFor('masters');
  const variants = useMasterVariants(selected.length > 0).data?.items ?? [];
  if (!selected.length) return null;
  return (
    <>
      {selected.map((tag) => {
        const label = `Has master: ${masterTagLabel(tag, variants) ?? 'removed variant'}`;
        return (
          <span className="chip" key={`masters:${tag}`} data-testid="master-filter-chip">
            {label}
            <button aria-label={`Remove filter ${label}`} onClick={() => f.toggle('masters', tag)}><X size={12} /></button>
          </span>
        );
      })}
    </>
  );
}
